import { z } from 'zod';
/**
 * Teléfonos celulares argentinos, normalizados a E.164.
 *
 * **Una sola regla para toda entrada de teléfono del sistema**, presente y
 * futura. La alternativa —que cada pantalla valide a su manera— termina con el
 * mismo número guardado de cuatro formas distintas, y el día que haya que
 * mandarle un WhatsApp no se sabe cuál de las cuatro es la buena.
 *
 * Lo que se persiste y lo que va a viajar a WhatsApp (E5) es **el normalizado**,
 * no lo que la persona tipeó. Lo que tipeó no se conserva: no aporta nada y es
 * PII de más.
 *
 * ## Qué se acepta
 *
 * Las formas en que efectivamente se escribe un celular en Argentina:
 * `11 5566 7788`, `011 15 5566-7788`, `(011) 15 5566 7788`, `+54 9 11 5566 7788`,
 * `0054 9 11 55667788`. Todas dan el mismo resultado: `+5491155667788`.
 *
 * ## Por qué el 9 siempre está en la salida
 *
 * En E.164, el `9` después del `54` es lo que distingue un celular de un fijo, y
 * es lo que WhatsApp exige. Este campo es de celulares —así lo pide el flujo del
 * cuidador y así lo va a usar la mensajería—, de modo que la salida siempre lo
 * lleva, lo haya escrito la persona o no.
 */
/** El `15` es prefijo de discado nacional para celulares; en E.164 no existe. */
const PREFIJO_MOVIL_NACIONAL = '15';
/** Longitud del número nacional significativo argentino: característica + abonado. */
const LARGO_NACIONAL = 10;
/**
 * Las características argentinas empiezan en 11, 2 o 3.
 *
 * Es lo que permite reconocer el `54` inicial como código de país sin
 * ambigüedad: ninguna característica local empieza con 5.
 */
const CARACTERISTICA_VALIDA = /^[123]/;
export function normalizarTelefonoAr(entrada) {
    let digitos = entrada.replace(/\D/g, '');
    if (digitos.length === 0)
        return null;
    // Prefijo internacional escrito como 00.
    if (digitos.startsWith('00'))
        digitos = digitos.slice(2);
    // Código de país.
    if (digitos.startsWith('54'))
        digitos = digitos.slice(2);
    // Prefijo de discado nacional.
    if (digitos.startsWith('0'))
        digitos = digitos.slice(1);
    // Indicador de celular de E.164: se saca acá y se repone al final, para que
    // dé lo mismo que la persona lo haya escrito o no.
    if (digitos.length > LARGO_NACIONAL && digitos.startsWith('9'))
        digitos = digitos.slice(1);
    // El 15 va después de la característica, que tiene 2, 3 o 4 dígitos.
    if (digitos.length === LARGO_NACIONAL + PREFIJO_MOVIL_NACIONAL.length) {
        for (const largoCaracteristica of [2, 3, 4]) {
            const posible = digitos.slice(largoCaracteristica, largoCaracteristica + 2);
            if (posible === PREFIJO_MOVIL_NACIONAL) {
                digitos =
                    digitos.slice(0, largoCaracteristica) +
                        digitos.slice(largoCaracteristica + PREFIJO_MOVIL_NACIONAL.length);
                break;
            }
        }
    }
    if (digitos.length !== LARGO_NACIONAL)
        return null;
    if (!CARACTERISTICA_VALIDA.test(digitos))
        return null;
    return `+549${digitos}`;
}
/**
 * El mensaje de error es uno solo y dice qué hacer, no qué está mal.
 *
 * "Formato inválido" no le sirve a nadie; un ejemplo sí. El público de esta app
 * son adultos mayores y sus familias, no gente acostumbrada a que un formulario
 * la rete.
 */
export const MENSAJE_TELEFONO_INVALIDO = 'Revisá el número: tiene que ser un celular con característica, como 11 5566 7788.';
/**
 * Schema de teléfono celular argentino.
 *
 * Transforma además de validar: lo que sale del `parse` ya está en E.164, así
 * que quien lo use no puede olvidarse de normalizar.
 */
export const telefonoArSchema = z
    .string()
    .min(6, MENSAJE_TELEFONO_INVALIDO)
    .max(30, MENSAJE_TELEFONO_INVALIDO)
    .transform((valor, ctx) => {
    const normalizado = normalizarTelefonoAr(valor);
    if (!normalizado) {
        ctx.addIssue({ code: 'custom', message: MENSAJE_TELEFONO_INVALIDO });
        return z.NEVER;
    }
    return normalizado;
});
//# sourceMappingURL=telefono.js.map